import { exactHash } from "./metrics";
import { saveQuarantineRecord } from "./storage";
import type { PhotoRecord, QuarantineRecord, QuarantineResult } from "./types";

export type QuarantineTarget = {
  groupId: string;
  photo: PhotoRecord;
  original: PhotoRecord;
  originalSelection: "manual" | "automatic";
};

type ManifestRow = {
  recordId: string;
  group: string;
  sourceLibrary: string;
  sourcePath: string;
  quarantinePath: string;
  sha256: string;
  bytes: number;
  status: string;
  timestamp: string;
  originalPhotoId: string;
  originalLibrary: string;
  originalPath: string;
  originalSha256: string;
  originalBytes: number;
  originalSelection: "manual" | "automatic";
};

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

async function permission(handle: FileSystemHandle, mode: "read" | "readwrite"): Promise<boolean> {
  if (await handle.queryPermission({ mode }) === "granted") return true;
  return await handle.requestPermission({ mode }) === "granted";
}

async function directoryFor(root: FileSystemDirectoryHandle, parts: string[]): Promise<FileSystemDirectoryHandle> {
  let current = root;
  for (const part of parts) current = await current.getDirectoryHandle(part, { create: true });
  return current;
}

async function availableName(directory: FileSystemDirectoryHandle, wanted: string): Promise<string> {
  const dot = wanted.lastIndexOf(".");
  const stem = dot > 0 ? wanted.slice(0, dot) : wanted;
  const extension = dot > 0 ? wanted.slice(dot) : "";
  for (let attempt = 0; attempt < 10_000; attempt += 1) {
    const name = attempt === 0 ? wanted : `${stem}_phototidy_${attempt}${extension}`;
    try {
      await directory.getFileHandle(name);
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotFoundError") return name;
      throw error;
    }
  }
  throw new Error(`Unable to find a safe destination name for ${wanted}`);
}

async function verifiedWrite(file: File, destination: FileSystemFileHandle, expectedHash: string): Promise<void> {
  const writable = await destination.createWritable();
  try {
    await writable.write(file);
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    throw error;
  }
  if (await exactHash(await destination.getFile()) !== expectedHash) throw new Error(`SHA-256 verification failed for ${file.name}`);
}

async function verifySelectedOriginal(original: PhotoRecord, duplicate: PhotoRecord): Promise<{ sha256: string; bytes: number }> {
  if (original.id === duplicate.id || await original.handle.isSameEntry(duplicate.handle)) {
    throw new Error(`The selected retained photo is the same file as ${duplicate.relativePath}; nothing was moved.`);
  }
  const expectedHash = original.exactHash ?? await exactHash(original.file);
  const currentHandle = await original.parentHandle.getFileHandle(original.name, { create: false });
  const currentFile = await currentHandle.getFile();
  if (currentFile.size !== original.file.size || await exactHash(currentFile) !== expectedHash) {
    throw new Error(`Selected retained photo changed: ${original.rootName}/${original.relativePath}; nothing was removed.`);
  }
  return { sha256: expectedHash, bytes: currentFile.size };
}

async function copyVerified(photo: PhotoRecord, targetRoot: FileSystemDirectoryHandle): Promise<{
  path: string;
  name: string;
  hash: string;
  parentHandle: FileSystemDirectoryHandle;
  fileHandle: FileSystemFileHandle;
}> {
  const pathParts = photo.relativePath.split("/");
  const fileName = pathParts.pop()!;
  const directoryParts = ["PhotoTidy_Quarantine", photo.rootName, ...pathParts];
  const directory = await directoryFor(targetRoot, directoryParts);
  const targetName = await availableName(directory, fileName);
  const destination = await directory.getFileHandle(targetName, { create: true });
  const sourceHash = photo.exactHash ?? await exactHash(photo.file);
  try {
    await verifiedWrite(photo.file, destination, sourceHash);
  } catch (error) {
    await directory.removeEntry(targetName).catch(() => undefined);
    throw error;
  }
  return { path: [...directoryParts, targetName].join("/"), name: targetName, hash: sourceHash, parentHandle: directory, fileHandle: destination };
}

export async function quarantinePhotos(
  targets: QuarantineTarget[],
  targetRoot: FileSystemDirectoryHandle,
  onStatus: (message: string) => void,
): Promise<QuarantineResult> {
  const selectedOriginalIds = new Set(targets.map((target) => target.original.id));
  const targetIds = new Set<string>();
  for (const target of targets) {
    if (selectedOriginalIds.has(target.photo.id)) {
      throw new Error(`Conflicting group choices: ${target.photo.relativePath} is selected as a retained photo elsewhere. Nothing was moved.`);
    }
    if (targetIds.has(target.photo.id)) {
      throw new Error(`The same photo was selected for quarantine more than once: ${target.photo.relativePath}. Nothing was moved.`);
    }
    targetIds.add(target.photo.id);
  }
  const manifest: ManifestRow[] = [];
  const errors: string[] = [];
  const recordIds: string[] = [];
  let moved = 0;
  let retained = 0;
  const manifestName = `phototidy-restore-manifest-${new Date().toISOString().replaceAll(":", "-")}.csv`;
  const manifestHandle = await targetRoot.getFileHandle(manifestName, { create: true });
  const header: Array<keyof ManifestRow> = [
    "recordId", "group", "sourceLibrary", "sourcePath", "quarantinePath", "sha256", "bytes", "status", "timestamp",
    "originalPhotoId", "originalLibrary", "originalPath", "originalSha256", "originalBytes", "originalSelection",
  ];
  const writeManifest = async (): Promise<void> => {
    const body = [header.join(","), ...manifest.map((row) => header.map((key) => csvCell(row[key as keyof ManifestRow])).join(","))].join("\n");
    const writer = await manifestHandle.createWritable();
    await writer.write(body);
    await writer.close();
  };
  await writeManifest();

  for (const { photo, groupId, original, originalSelection } of targets) {
    onStatus(`Securing ${photo.relativePath}`);
    try {
      const selectedOriginal = await verifySelectedOriginal(original, photo);
      const copied = await copyVerified(photo, targetRoot);
      const now = new Date().toISOString();
      const record: QuarantineRecord = {
        id: crypto.randomUUID(), groupId, photoId: photo.id, sourceLibrary: photo.rootName,
        sourcePath: photo.relativePath, sourceName: photo.name, sourceParentHandle: photo.parentHandle,
        quarantinePath: copied.path, quarantineName: copied.name, quarantineParentHandle: copied.parentHandle,
        quarantineFileHandle: copied.fileHandle, sha256: copied.hash, bytes: photo.file.size,
        originalPhotoId: original.id, originalLibrary: original.rootName, originalPath: original.relativePath,
        originalSha256: selectedOriginal.sha256, originalBytes: selectedOriginal.bytes, originalSelection,
        originalParentHandle: original.parentHandle, originalName: original.name,
        status: "source-retained", timestamp: now,
      };
      recordIds.push(record.id);
      const row: ManifestRow = {
        recordId: record.id, group: groupId, sourceLibrary: photo.rootName, sourcePath: photo.relativePath,
        quarantinePath: copied.path, sha256: copied.hash, bytes: photo.file.size,
        status: "VERIFIED_COPY_SOURCE_PRESENT", timestamp: now,
        originalPhotoId: original.id, originalLibrary: original.rootName, originalPath: original.relativePath,
        originalSha256: selectedOriginal.sha256, originalBytes: selectedOriginal.bytes, originalSelection,
      };
      manifest.push(row);
      await saveQuarantineRecord(record);
      await writeManifest();
      try {
        await verifySelectedOriginal(original, photo);
        const currentSource = await (await photo.parentHandle.getFileHandle(photo.name, { create: false })).getFile();
        if (currentSource.size !== photo.file.size || await exactHash(currentSource) !== copied.hash) {
          throw new Error(`Source photo changed after scanning: ${photo.rootName}/${photo.relativePath}; it was not removed.`);
        }
        await photo.parentHandle.removeEntry(photo.name);
        moved += 1;
        record.status = "quarantined";
        record.timestamp = new Date().toISOString();
        row.status = "MOVED_AFTER_SHA256_VERIFICATION";
        row.timestamp = record.timestamp;
        await saveQuarantineRecord(record);
        await writeManifest();
      } catch (error) {
        retained += 1;
        errors.push(`Copied but source retained: ${photo.relativePath}`);
        row.status = "COPIED_SOURCE_RETAINED";
        row.timestamp = new Date().toISOString();
        await writeManifest();
        console.warn(error);
      }
    } catch (error) {
      errors.push(error instanceof Error ? error.message : `Failed: ${photo.relativePath}`);
    }
  }
  return { moved, retained, failed: errors.length - retained, manifestName, errors, recordIds };
}

export async function restoreQuarantineRecord(record: QuarantineRecord): Promise<QuarantineRecord> {
  if (record.status !== "quarantined") throw new Error("Only a moved quarantine file can be restored.");
  if (!await permission(record.sourceParentHandle, "readwrite")) throw new Error("Access to the original folder was not granted.");
  if (!await permission(record.quarantineParentHandle, "readwrite")) throw new Error("Access to the quarantine folder was not granted.");
  const quarantinedFile = await record.quarantineFileHandle.getFile();
  if (await exactHash(quarantinedFile) !== record.sha256) throw new Error("The quarantine file has changed; restore was stopped.");
  const restoredName = await availableName(record.sourceParentHandle, record.sourceName);
  const restoredHandle = await record.sourceParentHandle.getFileHandle(restoredName, { create: true });
  try {
    await verifiedWrite(quarantinedFile, restoredHandle, record.sha256);
    await record.quarantineParentHandle.removeEntry(record.quarantineName);
  } catch (error) {
    await record.sourceParentHandle.removeEntry(restoredName).catch(() => undefined);
    throw error;
  }
  record.status = "restored";
  record.restoredPath = restoredName;
  record.timestamp = new Date().toISOString();
  await saveQuarantineRecord(record);
  return record;
}

export async function permanentlyDeleteQuarantineRecord(record: QuarantineRecord): Promise<QuarantineRecord> {
  if (record.status !== "quarantined" && record.status !== "source-retained") {
    throw new Error("Only an active quarantine file can be deleted.");
  }
  const retainedParent = record.status === "source-retained" ? record.sourceParentHandle : record.originalParentHandle;
  const retainedName = record.status === "source-retained" ? record.sourceName : record.originalName;
  const retainedHash = record.status === "source-retained" ? record.sha256 : record.originalSha256;
  const retainedBytes = record.status === "source-retained" ? record.bytes : record.originalBytes;
  if (!retainedParent || !retainedName || !retainedHash || retainedBytes === undefined) {
    throw new Error("This older quarantine record does not identify a verified retained photo. Restore it or review the original before deleting.");
  }
  if (!await permission(retainedParent, "read")) throw new Error("Read access to the retained photo was not granted.");
  const retainedFile = await (await retainedParent.getFileHandle(retainedName, { create: false })).getFile();
  if (retainedFile.size !== retainedBytes || await exactHash(retainedFile) !== retainedHash) {
    throw new Error("The selected retained photo is missing or changed; permanent deletion was stopped.");
  }
  if (!await permission(record.quarantineParentHandle, "readwrite")) throw new Error("Access to the quarantine folder was not granted.");
  const currentQuarantineHandle = await record.quarantineParentHandle.getFileHandle(record.quarantineName, { create: false });
  if (!await currentQuarantineHandle.isSameEntry(record.quarantineFileHandle)) {
    throw new Error("The quarantine file at this path was replaced; permanent deletion was stopped.");
  }
  const quarantineFile = await currentQuarantineHandle.getFile();
  if (quarantineFile.size !== record.bytes || await exactHash(quarantineFile) !== record.sha256) {
    throw new Error("The quarantine file changed; permanent deletion was stopped.");
  }
  await record.quarantineParentHandle.removeEntry(record.quarantineName);
  record.status = "deleted";
  record.timestamp = new Date().toISOString();
  await saveQuarantineRecord(record);
  return record;
}
